import { Campaign, CampaignType } from 'services/campaign/types';
import React, { useRef, useState } from 'react';
import { Form, FormRef, RadioGroup } from 'common/form';
import {
  EditEngagementCampaignDetails,
  editEngagementCampaignDetailsForm,
  EditJobCampaignDetails,
  editJobCampaignDetailsForm
} from '../forms/editCampaignDetailsForm';
import { ToastType, useToast } from 'services/toast';
import { Stack, Typography } from '@mui/material';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { useEditCampaignInfoMutation } from 'generated-graphql/hooks/apollo-hooks';
import { AnalysingLoadingGif } from 'common/animations/AnalysingLoadingGif';
import { CopyButton, FailedMessageWithHref } from 'common/ui';
import { Button } from 'system/atoms';

type Props = {
  campaign: Campaign;
};

export const EditCampaignDetailsDrawer: React.FC<Props> = ({ campaign }) => {
  const { closeDrawer: closeDrawerV2 } = useDrawerV2();
  const { showToast } = useToast();
  const engagementFormRef =
    useRef<FormRef<EditEngagementCampaignDetails>>(null);
  const jobFormRef = useRef<FormRef<EditJobCampaignDetails>>(null);

  const isEngagementOutreach =
    campaign.type === CampaignType.ENGAGEMENT_OUTREACH;

  const [disableNudging, setDisableNudging] = useState(
    campaign.disableNudging ?? false
  );

  const [editCampaignInfo, { loading }] = useEditCampaignInfoMutation({
    onCompleted: () => {
      showToast({
        type: ToastType.Success,
        title: isEngagementOutreach
          ? 'Campaign details updated'
          : 'Job details updated'
      });
      closeDrawerV2();
    }
  });

  const showErrorToast = () => {
    showToast({
      type: ToastType.Error,
      title: 'Something went wrong',
      message: (
        <FailedMessageWithHref emailSubject="Failed to update campaign details" />
      )
    });
  };

  const handleEngagementSubmit = async (
    values: EditEngagementCampaignDetails
  ) => {
    try {
      await editCampaignInfo({
        variables: {
          input: {
            campaignId: campaign.id,
            title: values.title,
            outreachDescription: values.outreachDescription || null,
            disableNudging
          }
        }
      });
    } catch (error) {
      showErrorToast();
    }
  };

  const handleJobSubmit = async (values: EditJobCampaignDetails) => {
    try {
      await editCampaignInfo({
        variables: {
          input: {
            title: values.jobTitle,
            campaignId: campaign.id,
            jobDescriptionParsed: {
              employerName: values.employerName,
              location: values.location,
              contractType: values.contractType || null
            },
            jobDescriptionText: values.jobDescriptionText,
            additionalContext: values.additionalContext || null,
            disableNudging
          }
        }
      });
    } catch (error) {
      showErrorToast();
    }
  };

  const getJobDetailsTextToCopy = () => {
    const values = jobFormRef.current?.getValues();
    if (!values) return;

    return `Job Title: ${values.jobTitle}

Employer Name: ${values.employerName}

Location: ${values.location}

Job Description:
${values.jobDescriptionText}${
      values.additionalContext
        ? `\n\nAdditional Information:\n${values.additionalContext}`
        : ''
    }`;
  };

  const getEngagementDetailsToCopy = () => {
    const values = engagementFormRef.current?.getValues();
    if (!values) return;

    return `Campaign Title: ${values.title}

Campaign Description:
${values.outreachDescription}`;
  };

  if (isEngagementOutreach) {
    return (
      <Stack direction="column" gap="20px">
        <Form
          ref={engagementFormRef}
          schema={editEngagementCampaignDetailsForm.schema}
          fields={editEngagementCampaignDetailsForm.fields}
          buttonContainerSx={{ display: 'none' }}
          defaultValues={{
            title: campaign.title || '',
            outreachDescription: campaign.outreachDescription || ''
          }}
          onSubmit={handleEngagementSubmit}
        />
        <RadioGroup
          name="disableNudging"
          value={disableNudging ? 'disabled' : 'enabled'}
          field={{
            label: 'Nudge non-responsive candidates',
            subLabel:
              "Candidates who don't respond receive up to three reminders, 24 hours apart.",
            options: [
              { label: 'Enabled', value: 'enabled' },
              { label: 'Disabled', value: 'disabled' }
            ]
          }}
          onChange={(value) => setDisableNudging(value === 'disabled')}
        />
        <Stack direction="row" gap={2}>
          <Button
            variant="primary"
            disabled={loading}
            loading={loading}
            onClick={() => engagementFormRef.current?.submit()}
          >
            Update
          </Button>
          <CopyButton valuesToCopyFunction={getEngagementDetailsToCopy} />
          <Button variant="tertiary" onClick={() => closeDrawerV2()}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="40px" py="20px">
      {campaign.jobDescriptionParsed?.summaryOfRole && (
        <Stack direction="row" gap={1}>
          <AnalysingLoadingGif width={20} height={20} />
          <Stack direction="column" gap={1}>
            <Typography fontWeight={600} color="primary.main">
              Popp's campaign summary
            </Typography>
            <Typography>
              {campaign.jobDescriptionParsed?.summaryOfRole}
            </Typography>
          </Stack>
        </Stack>
      )}

      <Form
        ref={jobFormRef}
        schema={editJobCampaignDetailsForm.schema}
        fields={editJobCampaignDetailsForm.fields}
        buttonContainerSx={{ display: 'none' }}
        defaultValues={{
          jobTitle:
            campaign.title || campaign.jobDescriptionParsed?.jobTitle || '',
          employerName: campaign.jobDescriptionParsed?.employerName || '',
          location: campaign.jobDescriptionParsed?.location || '',
          contractType: campaign.jobDescriptionParsed?.contractType || '',
          jobDescriptionText: campaign.jobDescriptionText || '',
          additionalContext: campaign.additionalContext || ''
        }}
        onSubmit={handleJobSubmit}
      />
      <RadioGroup
        name="disableNudging"
        value={disableNudging ? 'disabled' : 'enabled'}
        field={{
          label: 'Nudge non-responsive candidates',
          subLabel:
            "Candidates who don't respond receive up to three reminders, 24 hours apart.",
          options: [
            { label: 'Enabled', value: 'enabled' },
            { label: 'Disabled', value: 'disabled' }
          ]
        }}
        onChange={(value) => setDisableNudging(value === 'disabled')}
      />
      <Stack direction="row" gap={2}>
        <Button
          variant="primary"
          onClick={() => jobFormRef.current?.submit()}
          disabled={loading}
          loading={loading}
        >
          Update
        </Button>
        <CopyButton valuesToCopyFunction={getJobDetailsTextToCopy} />
        <Button variant="tertiary" onClick={() => closeDrawerV2()}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
